import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Input, Form, Select, message, Spin,
  Alert, Typography, Space, Tag, Tooltip, Steps
} from 'antd';
import {
  RobotOutlined, SettingOutlined, SendOutlined, ClearOutlined,
  LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  CopyOutlined, ThunderboltOutlined,
  WifiOutlined, ArrowRightOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIAssistantProps {
  visible: boolean;
  onClose: () => void;
  initialPrompt?: string;
  contextData?: string;
  title?: string;
  suggestedQuestions?: string[];
}

const PRESET_MODELS = [
  { label: '🔵 DeepSeek Chat', value: 'deepseek-web/deepseek-chat', desc: 'Free · Strong on Chinese' },
  { label: '🟣 Claude Sonnet', value: 'claude-web/claude-sonnet-4-6', desc: 'High-quality analysis' },
  { label: '🟢 ChatGPT 4o', value: 'chatgpt-web/gpt-4o', desc: 'Strong all-round' },
  { label: '🌙 Kimi', value: 'kimi-web/kimi', desc: 'Long context · Chinese' },
  { label: '💎 Gemini Flash', value: 'gemini-web/gemini-2.0-flash', desc: 'Fast' },
  { label: '☁️ Tongyi Qianwen', value: 'qwen-web/qwen-plus', desc: 'Alibaba · Chinese' },
  { label: '🧠 Zhipu GLM-4', value: 'glm-web/glm-4', desc: 'Domestic · Chinese' },
  { label: 'GPT-3.5-turbo', value: 'gpt-3.5-turbo', desc: 'OpenAI API' },
  { label: 'GPT-4o', value: 'gpt-4o', desc: 'OpenAI API' },
];

const DEFAULT_SUGGESTIONS = [
  'Analyse overall exam performance',
  'Which knowledge points do students struggle with most?',
  'Give targeted teaching improvement suggestions',
  'Generate a student performance analysis report',
];

const AIAssistant: React.FC<AIAssistantProps> = ({
  visible, onClose, initialPrompt, contextData, title = 'AI Assistant',
  suggestedQuestions
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'chat' | 'setup'>('chat');
  const [setupStep, setSetupStep] = useState(0);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [connResult, setConnResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [hasConfig, setHasConfig] = useState(false);
  const [config, setConfig] = useState({ endpoint_url: '', model_name: 'deepseek-web/deepseek-chat' });
  const [form] = Form.useForm();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('token');
  const sentInitial = useRef(false);

  const suggestions = suggestedQuestions || (contextData ? DEFAULT_SUGGESTIONS : [
    'Generate some fill-in-the-blank questions about functions',
    'How can I improve student engagement?',
    'Help me assess the difficulty of this exam',
    'Give me a study revision plan',
  ]);

  useEffect(() => {
    if (visible) {
      sentInitial.current = false;
      loadConfig();
    } else {
      setConnResult(null);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && hasConfig && initialPrompt && !sentInitial.current && messages.length === 0) {
      sentInitial.current = true;
      sendMessage(initialPrompt);
    }
  }, [visible, hasConfig]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await axios.get('/api/ai/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.endpoint_url) {
        setConfig(res.data);
        setHasConfig(true);
        form.setFieldsValue(res.data);
        setView('chat');
      } else {
        setHasConfig(false);
        setView('setup');
        setSetupStep(0);
      }
    } catch {
      setHasConfig(false);
      setView('setup');
      setSetupStep(0);
    } finally {
      setConfigLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const values = await form.validateFields();
      setConfigSaving(true);
      await axios.post('/api/ai/config', values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(values);
      setHasConfig(true);
      setView('chat');
      message.success('✅ AI configuration saved — ready to use!');
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setConfigSaving(false);
    }
  };

  const testConnection = async () => {
    const values = form.getFieldsValue();
    if (!values.endpoint_url) {
      message.warning('Please enter the endpoint URL first');
      return;
    }
    setTestingConn(true);
    setConnResult(null);
    try {
      const res = await axios.post('/api/ai/test-connection', values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setConnResult({ ok: true, msg: `Connected! AI replied: ${res.data.reply}` });
        setSetupStep(2);
      } else {
        setConnResult({ ok: false, msg: res.data.error || 'Connection failed' });
      }
    } catch (err: any) {
      setConnResult({ ok: false, msg: err.response?.data?.error || 'Network error' });
    } finally {
      setTestingConn(false);
    }
  };

  const sendMessage = async (text?: string) => {
    const content = (text || inputText).trim();
    if (!content) return;
    if (!hasConfig) { setView('setup'); return; }

    const userMsg: Message = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputText('');
    setLoading(true);

    try {
      const systemMsg = contextData
        ? `You are an intelligent education assistant helping teachers analyse exam data and provide teaching advice.\n\nCurrent exam data:\n${contextData}\n\nPlease give professional, specific suggestions based on the data.`
        : 'You are an intelligent education assistant helping teachers manage exams, analyse results, and provide teaching advice. Reply concisely and professionally.';

      const res = await axios.post('/api/ai/chat', {
        messages: [{ role: 'system', content: systemMsg }, ...newMessages]
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 70000
      });

      const reply = res.data?.choices?.[0]?.message?.content || '(No response from AI)';
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch (err: any) {
      const errMsg = err.response?.data?.error || 'Request failed — please check that OpenClaw is running';
      setMessages([...newMessages, { role: 'assistant', content: `❌ ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(() => message.success('Copied'));
  };

  const renderMessage = (msg: Message, idx: number) => {
    const isUser = msg.role === 'user';
    const isError = msg.content.startsWith('❌');
    return (
      <div key={idx} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 14 }}>
        {!isUser && (
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0, marginRight: 10, marginTop: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <RobotOutlined style={{ color: '#fff', fontSize: 16 }} />
          </div>
        )}
        <div style={{ maxWidth: '78%', position: 'relative' }}>
          <div style={{
            background: isUser
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : isError ? '#fff2f0' : '#f4f4f8',
            color: isUser ? '#fff' : isError ? '#cf1322' : '#1a1a2e',
            borderRadius: isUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
            padding: '10px 14px',
            fontSize: 14, lineHeight: 1.7,
            border: isError ? '1px solid #ffa39e' : 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</div>
          </div>
          {!isUser && !isError && (
            <Tooltip title="Copy reply">
              <Button
                type="text" size="small" icon={<CopyOutlined />}
                onClick={() => copyText(msg.content)}
                style={{ position: 'absolute', bottom: -4, right: 0, opacity: 0.5, fontSize: 11 }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderSetup = () => (
    <div style={{ padding: '24px 28px', overflowY: 'auto', maxHeight: 560 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', margin: '0 auto 12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <RobotOutlined style={{ fontSize: 32, color: '#fff' }} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a2e' }}>Set up AI Assistant</div>
        <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
          No API Key needed · Use your existing AI account · Completely free
        </div>
      </div>

      <Steps
        current={setupStep}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Install OpenClaw' },
          { title: 'Enter URL' },
          { title: 'Test Connection' },
        ]}
      />

      {setupStep === 0 && (
        <div>
          <Alert
            type="info"
            showIcon
            icon={<ThunderboltOutlined />}
            message={<strong>What is OpenClaw Zero Token?</strong>}
            description="It turns your AI account (ChatGPT, Claude, DeepSeek, etc.) into a local API endpoint. This system calls AI through that endpoint — no need to buy an API Key."
            style={{ marginBottom: 20 }}
          />
          <div style={{ background: '#fafafa', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: '#333' }}>📋 Setup Steps</div>
            {[
              { step: '1', text: 'Download OpenClaw Zero Token from GitHub', sub: 'github.com/linuxhsj/openclaw-zero-token', link: 'https://github.com/linuxhsj/openclaw-zero-token' },
              { step: '2', text: 'Install and start it (requires Chrome browser)', sub: 'It will listen on a local port — usually 3456' },
              { step: '3', text: 'Log in to the AI website you want to use in Chrome', sub: 'Supports: DeepSeek, Kimi, ChatGPT, Claude, and more' },
              { step: '4', text: 'Click Next and enter the endpoint URL', sub: 'Default: http://localhost:3456' },
            ].map(item => (
              <div key={item.step} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12, fontWeight: 700
                }}>{item.step}</div>
                <div>
                  <div style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{item.text}</div>
                  {item.link
                    ? <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#667eea' }}>{item.sub}</a>
                    : <div style={{ fontSize: 12, color: '#999' }}>{item.sub}</div>
                  }
                </div>
              </div>
            ))}
          </div>
          <Button type="primary" block size="large" icon={<ArrowRightOutlined />} onClick={() => setSetupStep(1)}
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: 8 }}>
            Already installed — Next
          </Button>
        </div>
      )}

      {setupStep >= 1 && (
        <div>
          <Form form={form} layout="vertical" initialValues={config}>
            <Form.Item
              label={<span style={{ fontWeight: 600 }}>OpenClaw Endpoint URL</span>}
              name="endpoint_url"
              rules={[{ required: true, message: 'Please enter the endpoint URL' }]}
              extra={<span style={{ color: '#888' }}>If OpenClaw is running locally, use http://localhost:3456. If you are using a tunnel (e.g. ngrok), use the public URL.</span>}
            >
              <Input
                size="large"
                placeholder="http://localhost:3456"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
            <Form.Item
              label={<span style={{ fontWeight: 600 }}>AI Model</span>}
              name="model_name"
              extra="Select the model that matches the AI account you logged into in OpenClaw"
            >
              <Select size="large" style={{ borderRadius: 8 }} placeholder="Select model">
                {PRESET_MODELS.map(m => (
                  <Option key={m.value} value={m.value}>
                    <Space>
                      <span>{m.label}</span>
                      <Tag color="default" style={{ fontSize: 11 }}>{m.desc}</Tag>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>

          {connResult && (
            <Alert
              type={connResult.ok ? 'success' : 'error'}
              showIcon
              icon={connResult.ok ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
              message={connResult.ok ? '🎉 Connected!' : '❌ Connection failed'}
              description={connResult.msg}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          )}

          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <Button
              block size="large" icon={testingConn ? <LoadingOutlined /> : <WifiOutlined />}
              onClick={testConnection} loading={testingConn}
              style={{ borderRadius: 8, borderColor: '#667eea', color: '#667eea' }}
            >
              {testingConn ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              type="primary" block size="large" icon={<CheckCircleOutlined />}
              onClick={saveConfig} loading={configSaving}
              disabled={!connResult?.ok && setupStep < 2}
              style={{
                background: connResult?.ok ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none', borderRadius: 8
              }}
            >
              {connResult?.ok ? '✅ Save & Start Using' : 'Save Configuration'}
            </Button>
            {setupStep > 0 && <Button block size="small" type="text" onClick={() => setSetupStep(0)} style={{ color: '#999' }}>← Back to setup guide</Button>}
          </Space>
        </div>
      )}
    </div>
  );

  const renderChat = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: 560 }}>
      {/* Status bar */}
      <div style={{
        padding: '8px 16px', borderBottom: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fafafa'
      }}>
        <Space size={6}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#52c41a' }} />
          <Text style={{ fontSize: 12, color: '#52c41a', fontWeight: 500 }}>Connected</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>· {config.model_name}</Text>
        </Space>
        <Space>
          <Tooltip title="Clear conversation">
            <Button size="small" icon={<ClearOutlined />} type="text"
              onClick={() => { setMessages([]); sentInitial.current = false; }}
              disabled={messages.length === 0}>Clear</Button>
          </Tooltip>
          <Button size="small" icon={<SettingOutlined />} type="text"
            onClick={() => { setView('setup'); setSetupStep(1); }}>Change Model</Button>
        </Space>
      </div>

      {/* Message area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', background: '#fff' }}>
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: 'center', paddingTop: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <RobotOutlined style={{ fontSize: 26, color: '#fff' }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>
              AI Assistant Ready
            </div>
            <div style={{ fontSize: 13, color: '#aaa', marginBottom: 20 }}>
              {contextData ? 'Exam data loaded — feel free to ask anything' : 'How can I help you?'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {suggestions.map(q => (
                <Button key={q} size="small" onClick={() => sendMessage(q)}
                  style={{ borderRadius: 16, borderColor: '#d9d0f0', color: '#764ba2', background: '#f8f6ff' }}>
                  {q}
                </Button>
              ))}
            </div>
          </div>
        )}
        {messages.map(renderMessage)}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', marginRight: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <LoadingOutlined style={{ color: '#fff', fontSize: 16 }} />
            </div>
            <div style={{
              background: '#f4f4f8', borderRadius: '4px 18px 18px 18px',
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8
            }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#764ba2', opacity: 0.4,
                    animation: `bounce 1.2s ${i * 0.2}s infinite`
                  }} />
                ))}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>AI is thinking...</Text>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <TextArea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
            style={{ borderRadius: 10, resize: 'none' }}
          />
          <Button
            type="primary" icon={<SendOutlined />}
            onClick={() => sendMessage()}
            loading={loading}
            disabled={!inputText.trim()}
            style={{
              height: 'auto', minHeight: 38, minWidth: 52,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none', borderRadius: 10
            }}
          />
        </div>
        <div style={{ marginTop: 6, color: '#bbb', fontSize: 11, textAlign: 'center' }}>
          Powered by <a href="https://github.com/linuxhsj/openclaw-zero-token" target="_blank" rel="noopener noreferrer" style={{ color: '#bbb' }}>OpenClaw Zero Token</a> · No API Key needed · Uses your own AI account
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <Space>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
          </div>
          <span style={{ fontWeight: 600 }}>{view === 'setup' ? 'Set up AI Assistant' : title}</span>
          {view === 'chat' && <Tag color="purple" style={{ marginLeft: 2, fontSize: 11 }}>OpenClaw</Tag>}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={680}
      footer={null}
      style={{ top: 20 }}
      styles={{ body: { padding: 0 } }}
    >
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
      {configLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ color: '#aaa', marginTop: 12, fontSize: 13 }}>Loading AI configuration...</div>
        </div>
      ) : view === 'setup' ? renderSetup() : renderChat()}
    </Modal>
  );
};

export default AIAssistant;
